import type { FibSettings } from '@erp/shared';
import { CreditCard, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { Button, FormField } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';

/**
 * FIB (First Iraqi Bank) online payment settings. Per-tenant merchant
 * credentials (the secret is stored encrypted, never returned). A "Test
 * connection" button fetches a FIB token to confirm the keys work.
 */
export function FibSettingsPage(): JSX.Element {
  const { t } = useTranslation('fib');
  const { t: tc } = useTranslation();
  const [s, setS] = useState<FibSettings | null>(null);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [baseUrl, setBaseUrl] = useState('https://fib.prod.fib.iq');
  const [realm, setRealm] = useState('fib-online-shop');
  const [iqdRate, setIqdRate] = useState('1450');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const hydrate = (v: FibSettings): void => {
    setS(v);
    setClientId(v.clientId ?? '');
    setBaseUrl(v.baseUrl);
    setRealm(v.realm);
    setIqdRate(String(v.iqdRate));
    setIsActive(v.isActive);
  };
  useEffect(() => { void api().get<FibSettings>('/fib/settings').then((r) => hydrate(r.data)).catch(() => undefined); }, []);

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      const body: Record<string, unknown> = { clientId: clientId.trim() || null, baseUrl: baseUrl.trim(), realm: realm.trim(), iqdRate: Number(iqdRate) || 1450, isActive };
      if (clientSecret.trim()) body.clientSecret = clientSecret.trim();
      const r = await api().put<FibSettings>('/fib/settings', body);
      hydrate(r.data);
      setClientSecret('');
      toast.success(tc('actions.save', { defaultValue: 'Saved' }));
    } catch { toast.error(tc('errors:server')); } finally { setSaving(false); }
  };

  const test = async (): Promise<void> => {
    setTesting(true);
    try { await api().post('/fib/test', {}); toast.success(t('tested', { defaultValue: 'Connection OK — FIB accepted your keys.' })); }
    catch { toast.error(t('testFailed', { defaultValue: 'FIB rejected the keys. Check the client id/secret.' })); }
    finally { setTesting(false); }
  };

  if (!s) return <Card className="p-6"><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-40 w-full" /></Card>;

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <CreditCard className="h-6 w-6 text-brand" aria-hidden />{t('title', { defaultValue: 'FIB payment' })}
        </h1>
        <p className="mt-1 text-sm text-fg-muted">{t('subtitle', { defaultValue: 'Accept FIB Online payments at storefront checkout. Get your merchant keys from First Iraqi Bank.' })}</p>
      </header>

      <Card className="flex flex-col gap-4 p-6">
        <FormField id="fib-cid" label={t('clientId', { defaultValue: 'Client ID' })}>
          <Input id="fib-cid" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="pg-your-store" />
        </FormField>
        <FormField id="fib-sec" label={t('clientSecret', { defaultValue: 'Client secret' })} helperText={s.hasSecret ? t('secretSet', { defaultValue: 'A secret is saved — leave blank to keep it.' }) : t('secretHint', { defaultValue: 'Stored encrypted; never shown again.' })}>
          <Input id="fib-sec" type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder={s.hasSecret ? '••••••••' : ''} autoComplete="new-password" />
        </FormField>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField id="fib-base" label={t('baseUrl', { defaultValue: 'API base URL' })} helperText={t('baseHint', { defaultValue: 'Production: fib.prod.fib.iq · Sandbox: fib.stage.fib.iq' })}>
            <Input id="fib-base" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
          </FormField>
          <FormField id="fib-realm" label={t('realm', { defaultValue: 'Realm' })}>
            <Input id="fib-realm" value={realm} onChange={(e) => setRealm(e.target.value)} />
          </FormField>
          <FormField id="fib-rate" label={t('iqdRate', { defaultValue: 'IQD rate (per 1 of base currency)' })} helperText={t('rateHint', { defaultValue: 'FIB charges in IQD; non-IQD order totals are converted with this rate.' })}>
            <Input id="fib-rate" type="number" value={iqdRate} onChange={(e) => setIqdRate(e.target.value)} />
          </FormField>
        </div>
        <label className="inline-flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-4 w-4 accent-[var(--color-brand)]" />
          {t('active', { defaultValue: 'Offer FIB at checkout' })}
        </label>
        <div className="flex gap-2">
          <Button onClick={save} loading={saving}>{tc('actions.save')}</Button>
          <Button variant="secondary" onClick={test} loading={testing} disabled={!s.hasSecret && !clientSecret.trim()}>
            <ShieldCheck className="h-4 w-4" aria-hidden />{t('test', { defaultValue: 'Test connection' })}
          </Button>
        </div>
      </Card>
    </div>
  );
}
